import { Injectable } from '@angular/core';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    gtag?: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TrackingService  {
  /**
   * Safely triggers a Meta Pixel event.
   * @param eventName Standard Meta Pixel event (e.g. 'AddToCart')
   * @param eventData Optional event data object (e.g. { value: 50, currency: 'USD' })
   */
  trackMeta(eventName: string, eventData: object = {}): void {
    if (typeof window.fbq === 'function') {
      window.fbq('track', eventName, eventData);
    } else {
      console.warn(`Meta Pixel not initialized: attempted to track "${eventName}"`);
    }
  }

  /**
   * Google Ads conversions IDs
   */
  private readonly PURCHASE_CONVERSION = 'AW-10839531407/YvIrCMq-pbEDEI-_2LAo';
  private readonly CART_CONVERSION = 'AW-10839531407/JqA-CIPQn7EDEI-_2LAo';
  private readonly CONTACT_CONVERSION = 'AW-10839531407/-UNCCKTvpbEDEI-_2LAo';
  private readonly NEWSLETTER_CONVERSION = 'AW-10839531407/IagZCOL5n7EDEI-_2LAo';
  private readonly GOBEVENTS_CONVERSION = 'AW-10839531407/b_TcCJqhprEDEI-_2LAo';

  /**
   * Generic product conversion helper
   * @param sendTo Google Ads conversion ID
   * @param eventData Optional event data object (e.g. { value: 50, currency: 'USD' })
   * @param urlRedirect Optional URL to redirect to
   */
  private trackGoogleProductConversion(sendTo: string, eventData: any = {}): void {

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: sendTo,
        ...eventData
      });

    } else {
      console.warn('Google Ads not initialized: conversion not sent', { sendTo, eventData });
    }
  }

  /**
   * Generic non-product conversion helper
   * @param url Optional URL to redirect to after conversion is sent
   * @remarks
   * This method is a simple wrapper around Google Ads' `gtag` function.
   * If the latter is not available, it will simply log a warning and redirect
   * to the given URL, if any.
   * @see https://support.google.com/google-ads/answer/6095883
   */
  private trackGoogleNonProductConversion(sendTo: string, url?: string): void {
    const gtag = (window as any).gtag;

    if (typeof gtag === 'function') {
      const callback = () => {
        if (url) {
          window.location.href = url;
        }
      };

      gtag('event', 'conversion', {
        send_to: sendTo,
        event_callback: callback
      });

    } else {
      console.warn('Google Ads not initialized: conversion not sent', { sendTo });
      if (url) {
        // fallback: still redirect
        window.location.href = url;
      }
    }
  }

  // Purchase [ok-payment.component.ts]
  trackPurchase(value: number, currency: string = 'EUR'): void {
    this.trackGoogleProductConversion(this.PURCHASE_CONVERSION, { value, currency });
  }

  // Add to cart [product-detail.component.ts]
  trackAddToCart(value: number, currency: string = 'EUR'): void {
    this.trackGoogleProductConversion(this.CART_CONVERSION, { value, currency });
  }

  // Contact [footer.component.ts]
  trackContact(urlRedirect?: string): void {
    const mailto = `mailto:demandes-clients@cavernedugobelin.fr`;
    this.trackGoogleNonProductConversion(this.CONTACT_CONVERSION, mailto);
  }

  // Newsletter [form-news.component.ts]
  trackNewsletter(urlRedirect?: string): void {
    this.trackGoogleNonProductConversion(this.NEWSLETTER_CONVERSION);
  }

  // GobEvents [home-anim.component.ts, anim-list.component.ts]
  trackGobEvents(urlRedirect?: string): void {
    this.trackGoogleNonProductConversion(this.GOBEVENTS_CONVERSION);
  }
}
